<template>
	<div class="roller-container">
		<div class="roller-selrow" :class="{ 'roller-unset': unset }">
			<span>{{ unsetValue }}</span>
		</div>
		<div class="roller" :class="{ 'roller-unset': unset }" ref="rollerElement" @scroll="updateScrollPos">
			<div class="roller-item roller-item-empty"></div>
			<div class="roller-item roller-item-empty"></div>
			<div v-for="(ri, idx) in list" class="roller-item" :style="scrollStyle(idx)">
				{{ ri.name }}
			</div>
			<div class="roller-item roller-item-empty"></div>
			<div class="roller-item roller-item-empty"></div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import type {PropType} from "vue";
import type {ListItemBase} from "@t/list/List";

const props = defineProps({
  list: {
    type: Array as PropType<ListItemBase[]>,
    default: []
  },
  modelValue: String,
  unsetValue: {
    type: String,
    default: "-",
  }
});
const emit = defineEmits(["update:modelValue"]);
const index = ref(0);
const scroll = ref(0);
const rollerElement = ref<HTMLDivElement | null>(null);

const unset = computed(() => {
  return props.modelValue == null; // already using the recommended approach
});


watch(
    () => props.modelValue,
    (n, o) => {
      if (n) scrollToValue(n);
    }
);
watch(
    () => index.value,
    (n, o) => {
      if (n < 0) emit("update:modelValue", null); // unset
      else {
        const item = props.list[n] as ListItemBase;
        emit("update:modelValue", item.value);
      }
    }
);
watch(
    () => unset.value,
    (n, o) => {
      if (rollerElement.value) rollerElement.value.scrollTop = 0;
    }
);


function updateScrollPos() {
  if (props.modelValue) {
    scroll.value = rollerElement.value ? rollerElement.value.scrollTop : 0;
    index.value = Math.floor(scroll.value / 32);
  }
}

function scrollStyle(idx:number) {
  const pos = idx * 32;
  const diff = 1 - Math.abs(scroll.value - pos) / 75;
  return {
    opacity: Math.max(diff, 0),
  };
}

function findValueIdx(val: string):number {
  let idx = 0;
  for (let item of props.list as ListItemBase[]) {
    if (val === item.value) return idx;
    idx++;
  }
  return -1;
}

function scrollToValue(listValue:string) {
  const idx = findValueIdx(listValue);
  const roller = rollerElement.value;
  if (roller == null) return;
  const currentIdx = Math.floor(roller.scrollTop / 32);
  if (idx >= 0 && idx !== currentIdx) {
    const scrollPos = idx * 32;
    roller.scroll({
      top: scrollPos,
      left: 0,
      behavior: "auto",
    });
  }
}

onMounted(() => {
  if (props.modelValue) scrollToValue(props.modelValue);
});
</script>

<style>
.roller-container {
	position: relative;
	min-width: 40px;
}
.roller {
	max-height: 160px;
	overflow-y: auto;
	scroll-snap-type: y mandatory;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	position: relative;
	margin: 0 4px;
	min-width: 32px;
}
.roller-selrow {
	position: absolute;
	background-color: white;
	border: var(--hair) solid var(--active);
	border-radius: 4px;
	width: 100%;
	height: 32px;
	line-height: 32px;
	top: 64px;
	left: 0;
	padding-left: 4px;
	padding-right: 4px;
}
.roller-selrow > span {
	visibility: hidden;
}
.roller-selrow.roller-unset > span {
	visibility: visible;
	color: var(--inactive);
}
.roller::-webkit-scrollbar {
	display: none;
}
.roller-item {
	height: 32px;
	min-height: 32px;
	line-height: 32px;
	scroll-snap-align: start;
	text-align: inherit;
	cursor: pointer;
}
.roller-unset > .roller-item {
	visibility: hidden;
}
.roller-item-empty {
}
</style>
